// Per-video colour, heading arrows and U-turn detection for the Map View.
//
// A video's `track` is a list of [lat, lon] samples taken across its whole
// duration (a 300 s clip carries 300 of them, so roughly one a second). The
// map draws the line, but a line alone does not say which way the vehicle was
// going — and on a road that is driven in both directions, or doubled back
// on, that is the only thing that matters. These helpers turn the samples
// into headings at a fixed time interval.

/**
 * Qualitative palette, one entry per video on screen.
 *
 * Route colours only say "this is video 3": they stay away from the hues the
 * status legend owns (green = day, indigo = night, red = corrupt, orange =
 * no GPS, magenta = not in database) so an orange route is never read as a
 * video without GPS. No yellows or light greens either, which disappear over
 * OpenStreetMap's pale roads and fields.
 */
export const VIDEO_COLORS = [
  "#0072B2", // blue
  "#7B3FA0", // purple
  "#00827F", // teal
  "#8C5A2B", // brown
  "#4A6FA5", // steel
  "#9C4F96", // plum
  "#1F3A93", // navy
  "#3C8DAD", // cyan
  "#6B5B95", // violet
  "#4F4F4F", // charcoal
];

export function videoColor(index) {
  return VIDEO_COLORS[((index % VIDEO_COLORS.length) + VIDEO_COLORS.length) % VIDEO_COLORS.length];
}

const toRad = (d) => (d * Math.PI) / 180;
const toDeg = (r) => (r * 180) / Math.PI;

/**
 * Initial bearing from a to b, in degrees clockwise from north (0–360).
 *
 * Great-circle rather than a flat atan2 of the differences: over a 5-minute
 * drive the error is small either way, but this stays correct at any latitude
 * and costs nothing.
 */
export function bearingBetween(a, b) {
  const [lat1, lon1] = a;
  const [lat2, lon2] = b;
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δλ = toRad(lon2 - lon1);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/** Smallest angle between two bearings, 0–180. */
export function angleBetween(a, b) {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

const samePoint = (a, b) => a[0] === b[0] && a[1] === b[1];

/**
 * One arrow every `everySeconds` of video.
 *
 * The step is derived from the sample rate rather than assumed, because the
 * sample count and the duration do not always agree — a truncated file can
 * carry a single sample for a 300 s clip.
 *
 * A heading needs two distinct points. When the vehicle is stopped the
 * samples repeat, so the look-ahead skips forward to the next point that
 * actually differs; if the whole window is stationary the arrow is dropped
 * rather than drawn pointing north by accident.
 */
export function headingArrows(item, everySeconds = 60) {
  const track = item?.track;
  if (!Array.isArray(track) || track.length < 2) return [];

  const duration = Number(item.durationSeconds) || track.length;
  const perSecond = track.length / duration;
  const step = Math.max(1, Math.round(everySeconds * perSecond));

  const arrows = [];
  for (let i = 0; i < track.length - 1; i += step) {
    const from = track[i];
    const limit = Math.min(i + step, track.length - 1);
    let j = i + 1;
    while (j <= limit && samePoint(from, track[j])) j += 1;
    if (j > limit) continue; // stationary for this whole minute
    arrows.push({
      lat: from[0],
      lon: from[1],
      bearing: bearingBetween(from, track[j]),
      second: Math.round(i / perSecond),
    });
  }
  return arrows;
}

/**
 * Points where the vehicle reversed direction.
 *
 * Compares each arrow's heading with the one before it; anything at or beyond
 * `thresholdDeg` is a turn-back. 140° rather than a strict 180° because a
 * U-turn sampled a minute apart rarely reads as an exact reversal, and the
 * approach and exit legs are often slightly offset.
 *
 * The turn is reported at the later arrow, which is where the new direction
 * is first travelled.
 */
export function detectUTurns(arrows, thresholdDeg = 140) {
  const turns = [];
  for (let i = 1; i < arrows.length; i += 1) {
    const delta = angleBetween(arrows[i - 1].bearing, arrows[i].bearing);
    if (delta >= thresholdDeg) {
      turns.push({
        lat: arrows[i].lat,
        lon: arrows[i].lon,
        delta: Math.round(delta),
        second: arrows[i].second,
      });
    }
  }
  return turns;
}

/** First and last fixes of a track, for the "1s" / "1e" labels. */
export function trackEnds(item) {
  const track = item?.track;
  if (Array.isArray(track) && track.length > 0) {
    const first = track[0];
    const last = track[track.length - 1];
    return {
      start: { lat: first[0], lon: first[1] },
      end: track.length > 1 ? { lat: last[0], lon: last[1] } : null,
    };
  }
  const start = item?.gps && item.gps.lat != null ? { lat: item.gps.lat, lon: item.gps.lon } : null;
  const end = item?.gpsEnd && item.gpsEnd.lat != null ? { lat: item.gpsEnd.lat, lon: item.gpsEnd.lon } : null;
  return { start, end };
}
